// ============================================================
// caseCloseActions.ts — "Close case…" / "Reopen…" for one case (the
// quick-view modal's footer action; tested in caseCloseActions.test.ts).
// CLOSING = a close date → status Closed: the case leaves the Open list
// (find it under the Closed filter; reopen there or here) and its deadline /
// task alerts stop — EXCEPT limitation deadlines, which keep alerting until
// they're marked complete (backend lib/deadlineWarnings
// deadlineAlertsSilenced). REOPENING clears the close date and the alerts
// resume. The server audits case.closed / case.reopened and only lets an
// admin, the owner or the handling team do either (lib/caseClose.ts).
// ============================================================
import { refusalText } from '../../utils/serverError'
import { updateCaseApi, updateCaseDeadlineApi } from '../../api/casevoApi'
import type { CaseDeadline } from '../../types/deadlines.types'
import { closeDateError } from '../../utils/caseDateRules'
import { textDateToIso } from '../../utils/civilDate'

const ukDate = (iso: string): string => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${y}` }
export const isLimitationDeadline = (d: Pick<CaseDeadline, 'deadlineType'>): boolean => /limitation/i.test(d.deadlineType?.name ?? '')

export interface CloseDialogDeadline { id: string; name: string; dueDate: string; isLimitation: boolean; note: string | null }
// The case's OPEN deadlines for the close dialog: limitation ones first (they
// keep alerting), then by due date.
export const closeDialogDeadlines = (deadlines: CaseDeadline[] | null | undefined): CloseDialogDeadline[] =>
  (deadlines ?? [])
    .filter((d) => !d.completed)
    .map((d) => {
      const lim = isLimitationDeadline(d)
      return {
        id: d.id, name: d.deadlineType?.name ?? 'Deadline', dueDate: d.dueDate.slice(0, 10), isLimitation: lim,
        note: lim ? `Limitation deadline still open (${ukDate(d.dueDate)}). It will keep sending alerts until it’s marked complete.` : null,
      }
    })
    .sort((a, b) => (a.isLimitation === b.isLimitation ? a.dueDate.localeCompare(b.dueDate) : a.isLimitation ? -1 : 1))

// Close on `closeDate` FIRST, then mark the ticked deadlines complete
// (nothing is ticked by default). If the close is refused (e.g. a 400 for a
// close date before the open date) it throws and NO deadline is touched. A
// deadline that then fails to complete is reported in `failed` (the case is
// already closed). Returns the case patch + the ids completed / failed.
export const closeCase = async (caseId: string, closeDate: string, completeDeadlineIds: string[] = []): Promise<{ patch: { closeDate: string }; completed: string[]; failed: string[]; failedWhy: string | null }> => {
  const patch = { closeDate }
  await updateCaseApi(caseId, patch)
  const completed: string[] = []
  const failed: string[] = []
  let failedWhy: string | null = null
  for (const id of completeDeadlineIds) {
    try { await updateCaseDeadlineApi(id, { completed: true }); completed.push(id) } catch (err) { failed.push(id); failedWhy = failedWhy ?? (refusalText(err, '') || null) }
  }
  return { patch, completed, failed, failedWhy }
}

// Reopen: clears the close date → Open (or Draft if setup was never
// finalised); its alerts resume.
export const reopenCase = async (caseId: string): Promise<{ closeDate: null }> => {
  const patch = { closeDate: null }
  await updateCaseApi(caseId, patch)
  return patch
}

// The close-date check for one or MANY cases (the case list's bulk close):
// not a half-typed / future date (closeDateError) and never before ANY of the
// cases' open dates — so the server's 400 can't refuse part of a batch. With
// several cases the message names the one whose open date is latest.
export const closeDateErrorForCases = (
  closeDate: string | null,
  cases: { openDate?: string | null; label?: string }[],
  today: string,
): string | null => {
  let latest: { iso: string; raw: string; label?: string } | null = null
  for (const c of cases) {
    const iso = textDateToIso(c.openDate ?? null)
    if (iso && (!latest || iso > latest.iso)) latest = { iso, raw: c.openDate as string, label: c.label }
  }
  const err = closeDateError(closeDate, latest?.raw ?? null, today)
  if (err && latest && cases.length > 1 && latest.label && err.startsWith('Close date is before the open date')) {
    return `Close date is before ${latest.label}’s open date (${ukDate(latest.iso)}).`
  }
  return err
}
